// Life & Wealth Solutions - Phase 5: Predictions Dashboard
// Phoenix Rescue Apps - "Your Agency to AI Today"
// Integrates with Phase 4 Predictive Insights Service

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  XCircle, Target, Clock, DollarSign, Users, Brain,
  RefreshCw, Filter, Search, ArrowRight, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PredictionsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRisk, setFilterRisk] = useState('all');
  
  // Predictions data
  const [leadPredictions, setLeadPredictions] = useState([]);
  const [churnRisks, setChurnRisks] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [predictionDetails, setPredictionDetails] = useState(null);

  useEffect(() => {
    fetchPredictions();
  }, []);

  const fetchPredictions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Fetch predictions from Phase 4 Predictive Insights Service
      const [highRiskChurn, topOpps] = await Promise.all([
        axios.get(`${API_URL}/predictive/churn/high-risk`, config),
        axios.get(`${API_URL}/predictive/opportunities/top?limit=20`, config)
      ]);

      setChurnRisks(highRiskChurn.data.customers || []);
      setOpportunities(topOpps.data.opportunities || []);
      
      // Fetch lead predictions
      await fetchLeadPredictions();
    } catch (error) {
      console.error('Failed to fetch predictions:', error);
      setDemoData();
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadPredictions = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Get all leads for batch prediction
      const leadsResponse = await axios.get(`${API_URL}/leads?status=active&limit=50`, config);
      const leads = leadsResponse.data.leads || [];

      if (leads.length > 0) {
        const leadIds = leads.map(lead => lead.lead_id);
        const batchResponse = await axios.post(
          `${API_URL}/predictive/leads/batch/conversion`,
          { lead_ids: leadIds },
          config
        );

        setLeadPredictions(batchResponse.data.predictions || []);
      }
    } catch (error) {
      console.error('Failed to fetch lead predictions:', error);
    }
  };

  const fetchLeadDetail = async (leadId) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(
        `${API_URL}/predictive/leads/${leadId}/conversion`,
        config
      );

      setPredictionDetails(response.data);
      setSelectedLead(leadId);
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
    }
  };

  const setDemoData = () => {
    setLeadPredictions([
      {
        lead_id: 'L1001',
        name: 'John Anderson',
        email: 'john.anderson@email.com',
        probability: 0.87,
        confidence: 'high',
        score: 92,
        estimated_days: 12,
        next_action: 'Schedule follow-up call',
        risk_level: 'low'
      },
      {
        lead_id: 'L1002',
        name: 'Sarah Martinez',
        email: 'sarah.m@email.com',
        probability: 0.76,
        confidence: 'high',
        score: 84,
        estimated_days: 18,
        next_action: 'Send product comparison',
        risk_level: 'medium'
      },
      {
        lead_id: 'L1003',
        name: 'Michael Chen',
        email: 'mchen@email.com',
        probability: 0.65,
        confidence: 'medium',
        score: 78,
        estimated_days: 25,
        next_action: 'Address pricing concerns',
        risk_level: 'medium'
      },
      {
        lead_id: 'L1004',
        name: 'Emily Davis',
        email: 'emily.d@email.com',
        probability: 0.42,
        confidence: 'medium',
        score: 61,
        estimated_days: 35,
        next_action: 'Re-engage with new offer',
        risk_level: 'high'
      },
      {
        lead_id: 'L1005',
        name: 'Robert Wilson',
        email: 'rwilson@email.com',
        probability: 0.91,
        confidence: 'high',
        score: 95,
        estimated_days: 8,
        next_action: 'Send contract',
        risk_level: 'low'
      }
    ]);

    setChurnRisks([
      {
        customer_id: 'C2001',
        name: 'Global Insurance Co',
        risk_score: 0.82,
        risk_level: 'high',
        factors: ['Payment delays', 'Reduced activity', 'Support tickets'],
        last_activity: '2025-11-28',
        policy_value: 125000,
        recommendation: 'Schedule urgent retention call'
      },
      {
        customer_id: 'C2002',
        name: 'Metropolitan Health',
        risk_score: 0.68,
        risk_level: 'medium',
        factors: ['Competitor contact', 'Price inquiry'],
        last_activity: '2025-12-05',
        policy_value: 87500,
        recommendation: 'Offer loyalty discount'
      },
      {
        customer_id: 'C2003',
        name: 'Sunshine Life Ltd',
        risk_score: 0.73,
        risk_level: 'high',
        factors: ['Contract ending soon', 'No recent engagement'],
        last_activity: '2025-11-15',
        policy_value: 156000,
        recommendation: 'Renewal conversation with special terms'
      }
    ]);

    setOpportunities([
      {
        opportunity_id: 'O3001',
        name: 'Enterprise Life Package',
        customer: 'TechCorp Industries',
        score: 94,
        value: 245000,
        probability: 0.89,
        close_date: '2025-12-20',
        stage: 'proposal',
        factors: ['Budget confirmed', 'Decision maker engaged', 'Strong fit']
      },
      {
        opportunity_id: 'O3002',
        name: 'Executive Health Plan',
        customer: 'Finance Group LLC',
        score: 87,
        value: 178000,
        probability: 0.76,
        close_date: '2025-12-28',
        stage: 'negotiation',
        factors: ['Multiple meetings', 'Urgent need', 'Competition low']
      },
      {
        opportunity_id: 'O3003',
        name: 'Small Business Bundle',
        customer: 'StartUp Ventures',
        score: 82,
        value: 92000,
        probability: 0.71,
        close_date: '2026-01-10',
        stage: 'proposal',
        factors: ['Quick decision timeline', 'Referral source']
      }
    ]);
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getRiskIcon = (risk) => {
    switch (risk) {
      case 'low': return <CheckCircle className="h-5 w-5" />;
      case 'medium': return <AlertTriangle className="h-5 w-5" />;
      case 'high': return <XCircle className="h-5 w-5" />;
      default: return <Target className="h-5 w-5" />;
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 0.75) return 'text-green-600';
    if (prob >= 0.50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const filteredLeads = leadPredictions.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterRisk === 'all' || lead.risk_level === filterRisk;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading predictions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Predictive Intelligence</h1>
          <p className="text-gray-500 mt-1">AI-Powered Lead Scoring & Risk Analysis</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchPredictions}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
            <Brain className="h-4 w-4" />
            <span>Run Analysis</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {['leads', 'churn', 'opportunities'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'leads' ? 'Lead Conversion' : tab === 'churn' ? 'Churn Risk' : 'Top Opportunities'}
            </button>
          ))}
        </nav>
      </div>

      {/* Lead Conversion Tab */}
      {activeTab === 'leads' && (
        <>
          {/* Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Risk Levels</option>
              <option value="low">Low Risk</option>
              <option value="medium">Medium Risk</option>
              <option value="high">High Risk</option>
            </select>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">High Probability</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {leadPredictions.filter(l => l.probability >= 0.75).length}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Medium Probability</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {leadPredictions.filter(l => l.probability >= 0.50 && l.probability < 0.75).length}
                  </p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <Target className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Low Probability</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {leadPredictions.filter(l => l.probability < 0.50).length}
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg. Close Time</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(leadPredictions.reduce((sum, l) => sum + l.estimated_days, 0) / leadPredictions.length || 0)} days
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Lead Predictions Table */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversion Probability</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Close</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Action</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLeads.map((lead) => (
                    <tr key={lead.lead_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                          <div className="text-sm text-gray-500">{lead.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-lg font-bold text-gray-900">{lead.score}</span>
                          <span className="text-sm text-gray-500 ml-1">/100</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-full max-w-xs">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-sm font-medium ${getProbabilityColor(lead.probability)}`}>
                                {(lead.probability * 100).toFixed(0)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  lead.probability >= 0.75 ? 'bg-green-600' :
                                  lead.probability >= 0.50 ? 'bg-yellow-600' :
                                  'bg-red-600'
                                }`}
                                style={{ width: `${lead.probability * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lead.estimated_days} days
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskColor(lead.risk_level)}`}>
                          {getRiskIcon(lead.risk_level)}
                          <span className="ml-1 capitalize">{lead.risk_level}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lead.next_action}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => fetchLeadDetail(lead.lead_id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Churn Risk Tab */}
      {activeTab === 'churn' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">High Risk</p>
                  <p className="text-2xl font-bold text-red-600">
                    {churnRisks.filter(c => c.risk_level === 'high').length}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total At Risk Value</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${churnRisks.reduce((sum, c) => sum + c.policy_value, 0).toLocaleString()}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Customers At Risk</p>
                  <p className="text-2xl font-bold text-gray-900">{churnRisks.length}</p>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {churnRisks.map((customer) => (
              <div key={customer.customer_id} className="bg-white rounded-lg shadow border border-l-4 border-l-red-600 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{customer.name}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        customer.risk_level === 'high' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {customer.risk_level.toUpperCase()} RISK
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Risk Score</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-red-600 h-2 rounded-full"
                              style={{ width: `${customer.risk_score * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-red-600">
                            {(customer.risk_score * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Policy Value</p>
                        <p className="text-lg font-bold text-gray-900 mt-1">
                          ${customer.policy_value.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-sm text-gray-500 mb-2">Risk Factors:</p>
                      <div className="flex flex-wrap gap-2">
                        {customer.factors.map((factor, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                            {factor}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-blue-900">Recommended Action:</p>
                      <p className="text-sm text-blue-700 mt-1">{customer.recommendation}</p>
                    </div>
                  </div>
                  <div className="ml-4">
                    <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2">
                      <span>Take Action</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Opportunities Tab */}
      {activeTab === 'opportunities' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Top Opportunities</p>
                  <p className="text-2xl font-bold text-gray-900">{opportunities.length}</p>
                </div>
                <Target className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Pipeline Value</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${opportunities.reduce((sum, o) => sum + o.value, 0).toLocaleString()}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg. Score</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(opportunities.reduce((sum, o) => sum + o.score, 0) / opportunities.length || 0)}
                  </p>
                </div>
                <Award className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Expected Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${Math.round(opportunities.reduce((sum, o) => sum + (o.value * o.probability), 0)).toLocaleString()}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opportunity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Probability</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Close Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {opportunities.map((opp) => (
                    <tr key={opp.opportunity_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{opp.name}</div>
                          <div className="text-sm text-gray-500">{opp.customer}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-2xl font-bold text-green-600">{opp.score}</span>
                          <span className="text-sm text-gray-500 ml-1">/100</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ${opp.value.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          Expected: ${Math.round(opp.value * opp.probability).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-32">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-green-600">
                              {(opp.probability * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full"
                              style={{ width: `${opp.probability * 100}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {opp.stage}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(opp.close_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button className="text-blue-600 hover:text-blue-900">
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Prediction Details Modal */}
      {selectedLead && predictionDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Prediction Details</h2>
              <button
                onClick={() => setSelectedLead(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Positive Factors</h3>
                {predictionDetails.positive_factors?.map((factor, idx) => (
                  <div key={idx} className="flex items-center text-sm text-green-700 mb-1">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {factor}
                  </div>
                ))}
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Negative Factors</h3>
                {predictionDetails.negative_factors?.map((factor, idx) => (
                  <div key={idx} className="flex items-center text-sm text-red-700 mb-1">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    {factor}
                  </div>
                ))}
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">AI Recommendation</h3>
                <p className="text-sm text-blue-700">{predictionDetails.ai_insight}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictionsDashboard;
